import { z } from 'zod';

const groupsStringSchema = z.string().regex(/^\[(?:[^\s[\]",]+(?: [^\s[\]",]+)*)?\]$/);

const groupsListStringSchema = z.string().superRefine((value, context) => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed) || !parsed.every((entry) => typeof entry === 'string')) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'must encode a JSON string array' });
    }
  } catch {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'must encode a JSON string array' });
  }
});

/**
 * Fleet-wide DualAuth context. API Gateway authorizer context values are strings;
 * these encodings are consumed directly by 15+ legacy services and are therefore
 * deliberately not normalized into booleans or arrays.
 */
export const accountsAuthorizerContextSchema = z
  .object({
    provider: z.string(),
    email: z.string(),
    username: z.string(),
    groups: groupsStringSchema,
    groupsList: groupsListStringSchema,
    readOnly: z.enum(['true', 'false']),
    sub: z.string(),
    orgId: z.string(),
    orgRole: z.string(),
    orgSlug: z.string(),
  })
  .strict();
export type AccountsAuthorizerContext = z.infer<typeof accountsAuthorizerContextSchema>;

/** Clerk v1 organization claims, before conversion to the authorizer context. */
export const clerkV1OrganizationClaimsSchema = z
  .object({
    org_id: z.string(),
    org_role: z.string(),
    org_slug: z.string(),
  })
  .strict();

/** Clerk v2 compact organization claims. A bare role is normalized by the adapter. */
export const clerkV2OrganizationClaimsSchema = z
  .object({
    id: z.string(),
    rol: z.string(),
    slg: z.string(),
  })
  .strict();

export const clerkOrganizationClaimsSchema = z.union([
  clerkV1OrganizationClaimsSchema.transform((claims) => ({
    orgId: claims.org_id,
    orgRole: claims.org_role,
    orgSlug: claims.org_slug,
  })),
  clerkV2OrganizationClaimsSchema.transform((claims) => ({
    orgId: claims.id,
    orgRole: claims.rol.startsWith('org:') ? claims.rol : `org:${claims.rol}`,
    orgSlug: claims.slg,
  })),
]);

/**
 * Organization-bearing JWT fields inspected by DualAuth. `aud` is intentionally
 * unconstrained: the legacy validator omits jose's audience option, so strings,
 * arrays, absent values, and mismatches are all accepted.
 */
export const clerkTokenOrganizationClaimsSchema = z
  .object({
    o: clerkV2OrganizationClaimsSchema.optional(),
    org_id: z.string().optional(),
    org_role: z.string().optional(),
    org_slug: z.string().optional(),
    aud: z.unknown().optional(),
  })
  .strict();

/** REST API v1 authorizer policies must wildcard both method and resource for cache safety. */
export const accountsRestV1WildcardResourceSchema = z
  .string()
  .regex(/^arn:aws:execute-api:[^:]+:\d{12}:[^/]+\/[^/]+\/\*\/\*$/);
